/* site/siteSearch.js — the public site's search.
 *
 * The prototype ships a placeholder ("search index not yet connected"); this is the real thing:
 * one in-memory index over everything the site actually publishes — all 89 pieces, the 25 category
 * listings, the four product worlds, the material families, the projects and the standalone pages.
 *
 * The index is built from the SAME content modules the pages render, so it can never drift from
 * what is on the site, and it is loaded with a DYNAMIC import the first time someone opens search.
 * That matters: content/piece.js is ~228 KB, and a static import here would put it in the main
 * bundle on every page load. This way it is fetched once, on demand.
 */

let cache = null;

const norm = (s) => String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
const titleCase = (s) => String(s || "").replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/** Build (once) the flat list of searchable entries. */
export async function loadIndex() {
  if (cache) return cache;
  const [catalogMod, pieceMod, materialsMod, projectsMod] = await Promise.all([
    import("./content/catalog"),
    import("./content/piece"),
    import("./content/materials"),
    import("./content/projects"),
  ]);
  const CATALOG = catalogMod.default || {};
  const PIECE = pieceMod.default || {};
  const MATERIALS = materialsMod.default || {};
  const PROJECTS = projectsMod.default || {};
  const rows = [];
  const push = (r) => { if (r.label && r.route) rows.push(r); };

  /* pieces — the 89 detail pages */
  (PIECE.items || []).forEach((it) => push({
    label: it.name,
    sub: [titleCase(it.category), titleCase(it.world)].filter(Boolean).join(" · "),
    route: it.route,
    kind: "Products",
    rank: 0,
  }));

  /* category listings and world hubs */
  Object.keys(CATALOG).forEach((key) => {
    const entry = CATALOG[key] || {};
    if (!entry.title) return;
    const isHub = key.indexOf("/") === -1;
    push({
      label: String(entry.title).replace(/\.$/, ""),
      sub: isHub ? "Product world" : titleCase(key.split("/")[0]),
      route: "/" + key,
      kind: isHub ? "Worlds" : "Categories",
      rank: isHub ? 1 : 2,
    });
  });

  /* material families */
  (MATERIALS.materials || []).forEach((m) => push({
    label: m.name, sub: m.desc || "Material", route: "/materials/" + m.id, kind: "Materials", rank: 3,
  }));

  /* projects (the prototype's are flagged placeholders — searchable, honestly labelled) */
  (PROJECTS.projects || []).forEach((p) => push({
    label: p.name, sub: [p.type, p.location].filter(Boolean).join(" · "), route: "/projects",
    kind: "Projects", rank: 5,
  }));

  /* the standalone pages */
  [
    ["The 2026 Collection", "Collections", "/collections"],
    ["Materials", "All material families", "/materials"],
    ["Projects", "Furniture in context", "/projects"],
    ["Contract", "For considered projects", "/contract"],
    ["About", "Lugano Home Collection", "/about"],
    ["Contact", "Contact LHC", "/contact"],
    ["Professional Resources", "CAD, BIM and technical documents", "/resources"],
    ["Partner Login", "For registered LHC partners", "/partners/login"],
    ["Become a Partner", "Apply for a partner account", "/partners/apply"],
    ["Legal", "Privacy, terms and cookies", "/legal"],
  ].forEach(([label, sub, route]) => push({ label, sub, route, kind: "Pages", rank: 4 }));

  rows.forEach((r) => { r._n = norm(r.label); r._s = norm(r.sub); });
  cache = rows;
  return cache;
}

/**
 * Rank matches for `query`. Exact prefix beats a word start, which beats a substring; the label
 * always beats the subtitle, and ties fall back to the entry kind's own order (products first).
 */
export function search(rows, query, limit = 12) {
  const q = norm(query).trim();
  if (!q) return [];
  const scored = [];
  for (const r of rows) {
    let score = -1;
    if (r._n === q) score = 100;
    else if (r._n.startsWith(q)) score = 80;
    else if (new RegExp("\\b" + q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).test(r._n)) score = 60;
    else if (r._n.includes(q)) score = 40;
    else if (r._s.includes(q)) score = 20;
    if (score >= 0) scored.push({ r, score: score - (r.rank || 0) });
  }
  scored.sort((a, b) => b.score - a.score || a.r.label.localeCompare(b.r.label));
  return scored.slice(0, limit).map((x) => x.r);
}

/** Group ranked results by their kind, preserving rank order. */
export function groupResults(results) {
  const groups = [];
  results.forEach((r) => {
    const g = groups.find((x) => x.kind === r.kind);
    if (g) g.items.push(r); else groups.push({ kind: r.kind, items: [r] });
  });
  return groups;
}

/** test seam — drop the memoised index */
export function __resetIndex() { cache = null; }
